import { AlertCircle, Download, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import type { ParsedCsvResult } from '@/types/massMigration';
import { columnLabel } from './csvColumns';

// ─── Download helper ──────────────────────────────────────────────────────────

function downloadErrorReport(result: ParsedCsvResult): void {
  const rowErrorMap = result.errors.reduce<Record<number, string[]>>((acc, e) => {
    const existing = acc[e.rowNumber] ?? [];
    const label = columnLabel(e.column);
    existing.push(label !== null ? `${label}: ${e.message}` : e.message);
    acc[e.rowNumber] = existing;
    return acc;
  }, {});

  const header = 'rowNumber,errors\n';
  const rows = Object.entries(rowErrorMap)
    .map(([rowNum, msgs]) => `${rowNum},"${msgs.join(' | ')}"`)
    .join('\n');
  const csv = header + rows;
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'error-report.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── UploadPreviewB — Compact ─────────────────────────────────────────────────

export interface UploadPreviewBProps {
  result: ParsedCsvResult;
  fileName: string;
  onReplace: () => void;
}

export function UploadPreviewB({ result, fileName, onReplace }: UploadPreviewBProps) {
  const hasErrors = result.errors.length > 0;

  // rowNumber 0 = file-level errors (missing required column, row cap exceeded);
  // they match no data row, so they render in the alert instead.
  const fileErrors = result.errors.filter((e) => e.rowNumber === 0);
  const rowErrors = result.errors.filter((e) => e.rowNumber > 0);
  const errorRowNumbers = new Set(rowErrors.map((e) => e.rowNumber));
  const errorByRow = rowErrors.reduce<Record<number, string>>((acc, e) => {
    acc[e.rowNumber] = acc[e.rowNumber] !== undefined
      ? `${acc[e.rowNumber]}; ${e.message}`
      : e.message;
    return acc;
  }, {});

  const validCount = result.rows.filter((r) => !errorRowNumbers.has(r.rowNumber)).length;

  const sortedRows = [...result.rows].sort((a, b) => {
    const aErr = errorRowNumbers.has(a.rowNumber) ? 0 : 1;
    const bErr = errorRowNumbers.has(b.rowNumber) ? 0 : 1;
    return aErr - bErr;
  });

  const stats = [
    { label: 'Total', value: result.totalRows },
    { label: 'Valid', value: validCount },
    { label: 'Errors', value: result.errors.length },
  ];

  return (
    <div className="space-y-4">
      {/* Stat tiles */}
      <div className="grid grid-cols-3 gap-3">
        {stats.map((s) => (
          <div
            key={s.label}
            className="flex flex-col items-center rounded-md border border-border bg-muted/30 p-3"
          >
            <span className="text-xl font-bold text-foreground">{s.value}</span>
            <span className="text-xs text-muted-foreground">{s.label}</span>
          </div>
        ))}
      </div>

      {hasErrors && (
        <Alert variant="warning">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>
            {result.errors.length} {result.errors.length === 1 ? 'error' : 'errors'} found
          </AlertTitle>
          <AlertDescription className="space-y-2">
            {fileErrors.length > 0 && (
              <ul className="space-y-0.5" aria-label="File errors">
                {fileErrors.map((e) => {
                  const label = columnLabel(e.column);
                  return (
                    <li key={`${e.column}-${e.message}`} className="text-sm">
                      {label !== null && <span className="font-medium">{label}: </span>}
                      {e.message}
                    </li>
                  );
                })}
              </ul>
            )}
            <p>Fix or exclude these rows before continuing.</p>
          </AlertDescription>
        </Alert>
      )}

      {/* Compact row list */}
      <div className="max-h-[400px] space-y-1 overflow-y-auto">
        {sortedRows.map((row) => {
          const errMsg = errorByRow[row.rowNumber];
          const hasRowError = errMsg !== undefined;
          const dotClass = hasRowError ? 'bg-destructive' : 'bg-success';
          return (
            <div
              key={row.rowNumber}
              className="flex items-center gap-3 rounded-md border border-border px-3 py-2 text-sm"
            >
              <span className="w-6 text-right text-xs text-muted-foreground">
                {row.rowNumber}
              </span>
              <span
                className={cn('h-2 w-2 flex-shrink-0 rounded-full', dotClass)}
                aria-hidden="true"
              />
              <span className="flex-1 font-mono text-xs text-foreground">
                {row.bridgeId}
              </span>
              {row.orgName !== null && (
                <span className="text-xs text-muted-foreground">{row.orgName}</span>
              )}
              {hasRowError && (
                <span className="text-xs text-destructive">{errMsg}</span>
              )}
            </div>
          );
        })}
      </div>

      {/* File name */}
      <p className="text-xs text-muted-foreground">{fileName}</p>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        {hasErrors && (
          <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={() => downloadErrorReport(result)}
          >
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            Download error report
          </Button>
        )}
        <Button variant="outline" size="sm" type="button" onClick={onReplace}>
          <RotateCcw className="mr-2 h-4 w-4" aria-hidden="true" />
          Replace file
        </Button>
      </div>
    </div>
  );
}
